/**
 * Zod Schemas for Embedding and Chunk Types
 *
 * Provides runtime validation schemas for Chunk, ChunkMetadata,
 * EmbeddingConfig, and Vector filtering.
 *
 * @module schemas/embedding.zod
 * @see SPEC-011 Requirements 2.1, 3.1, 5.3
 */
import { z } from 'zod';
// ============================================================================
// Embedding Provider & Config Schemas
// ============================================================================
/**
 * Schema for embedding provider types.
 * @see SPEC-011 Requirement 3.2
 */
export const EmbeddingProviderSchema = z.enum([
    'upstash_vector',
    'openai',
    'azure_openai',
    'local',
]);
/**
 * Schema for text normalization configuration.
 * @see SPEC-011 Requirement 3.1
 */
export const TextNormalizationConfigSchema = z.object({
    normalizeNewlines: z.boolean(),
    trimTrailingSpaces: z.boolean(),
    lowercaseAscii: z.boolean(),
});
/**
 * Schema for embedding configuration.
 * @see SPEC-011 Requirement 3.1
 */
export const EmbeddingConfigSchema = z.object({
    provider: EmbeddingProviderSchema,
    modelId: z.string().min(1),
    dimensions: z.number().int().positive(),
    normalize: z.boolean(),
    maxCharsPerInput: z.number().int().positive(),
    batchSize: z.number().int().positive(),
    maxConcurrency: z.number().int().positive(),
    textNormalization: TextNormalizationConfigSchema,
});
// ============================================================================
// Chunk Schemas
// ============================================================================
/**
 * Schema for chunk visibility levels.
 */
export const ChunkVisibilitySchema = z.enum(['private', 'shared', 'public']);
/**
 * Regex pattern for chunk ID validation.
 * Format: `{docId}:{ordinal}:{hash8}`
 * - docId: UUID format
 * - ordinal: non-negative integer
 * - hash8: 8 hex characters
 */
const CHUNK_ID_PATTERN = /^[a-f0-9-]+:\d+:[a-f0-9]{8}$/;
/**
 * Schema for document chunk.
 * @see SPEC-011 Requirement 2.1
 */
export const ChunkSchema = z.object({
    chunkId: z.string().regex(CHUNK_ID_PATTERN, {
        message: 'chunkId must match format: {docId}:{ordinal}:{hash8}',
    }),
    docId: z.string().uuid(),
    tenantId: z.string().min(1),
    ordinal: z.number().int().nonnegative(),
    text: z.string(),
    chunkHash: z.string().length(64), // SHA256 hex = 64 chars
    embeddingConfigId: z.string().length(64),
    updatedAtMs: z.number().int().positive(),
    visibility: ChunkVisibilitySchema,
});
/**
 * Schema for chunk metadata stored in vector index.
 * @see SPEC-011 Requirement 5.2
 */
export const ChunkMetadataSchema = z.object({
    docId: z.string().uuid(),
    tenantId: z.string().min(1),
    ordinal: z.number().int().nonnegative(),
    visibility: ChunkVisibilitySchema,
    chunkHash: z.string().length(64),
    embeddingConfigId: z.string().length(64),
    updatedAtMs: z.number().int().positive(),
});
/**
 * Schema for chunk with vector.
 */
export const ChunkWithVectorSchema = ChunkSchema.extend({
    vector: z.array(z.number()),
});
// ============================================================================
// Vector Filter Schemas
// ============================================================================
/**
 * Schema for vector search filter.
 * Used to filter vectors by tenant, visibility, and document.
 * @see SPEC-011 Requirement 5.3
 */
export const VectorFilterSchema = z.object({
    /** Required: tenant ID for data isolation */
    tenantId: z.string().min(1),
    /** Optional: filter by visibility level */
    visibility: ChunkVisibilitySchema.optional(),
    /** Optional: filter to specific document */
    docId: z.string().uuid().optional(),
    /** Optional: exclude specific documents from results */
    excludeDocIds: z.array(z.string().uuid()).optional(),
});
// ============================================================================
// Embedding Job Schemas
// ============================================================================
/**
 * Schema for embedding job trigger reasons.
 * @see SPEC-011 Requirement 6.2
 */
export const EmbeddingReasonSchema = z.enum(['docSaved', 'aiApplied', 'manualReindex']);
/**
 * Schema for embedding job status.
 * @see SPEC-011 Requirement 6.1
 */
export const EmbeddingJobStatusSchema = z.enum(['queued', 'running', 'completed', 'failed']);
/**
 * Schema for embedding job result.
 * @see SPEC-011 Requirement 6.4
 */
export const EmbeddingJobResultSchema = z.object({
    jobId: z.string().uuid(),
    docId: z.string().uuid(),
    status: EmbeddingJobStatusSchema,
    chunksProcessed: z.number().int().nonnegative(),
    chunksSkipped: z.number().int().nonnegative(),
    latencyMs: z.number().int().nonnegative(),
    error: z.string().optional(),
});
/**
 * Schema for embedding job state stored in Redis.
 * @see SPEC-011 Requirement 6.1
 */
export const EmbeddingJobStateSchema = z.object({
    jobId: z.string().uuid(),
    docId: z.string().uuid(),
    tenantId: z.string().min(1),
    reason: EmbeddingReasonSchema,
    status: EmbeddingJobStatusSchema,
    createdAtMs: z.number().int().positive(),
    updatedAtMs: z.number().int().positive(),
    chunksProcessed: z.number().int().nonnegative().optional(),
    chunksSkipped: z.number().int().nonnegative().optional(),
    error: z.string().optional(),
});
//# sourceMappingURL=embedding.zod.js.map